import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useMemo, useState } from 'react'
import { type ManagementBuildingRow } from '../../../../models/peerToPeer/Management'
import { BuildingActions } from './BuildingActions'
import { StatusChip } from './StatusChip'
import { styles } from './BuildingTable.styles'

const HEADERS = ['Submitted By', 'ISD', 'District', 'Building', 'Status']

const asTime = (value: string | null): number => value != null ? new Date(value).getTime() : 0

interface Props {
  rows: ManagementBuildingRow[]
  reportingYearId: number
  onChanged: () => void
}

export const BuildingTable = ({ rows, reportingYearId, onChanged }: Props): JSX.Element => {
  const [newestFirst, setNewestFirst] = useState(true)

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => newestFirst
      ? asTime(b.lastUpdatedAt) - asTime(a.lastUpdatedAt)
      : asTime(a.lastUpdatedAt) - asTime(b.lastUpdatedAt)),
    [rows, newestFirst]
  )

  return (
    <Box sx={styles.scroller}>
      <Table size='small' data-testid='p2pBuildingTable'>
        <TableHead>
          <TableRow>
            {HEADERS.map(header => <TableCell key={header} sx={styles.headCell}>{header}</TableCell>)}
            <TableCell
              sx={styles.sortableHeadCell} data-testid='p2pSortLastUpdated'
              onClick={() => { setNewestFirst(!newestFirst) }}
            >
              <Stack direction='row' alignItems='center' gap='2px'>
                Last Updated
                {newestFirst ? <KeyboardArrowDownIcon fontSize='inherit' /> : <KeyboardArrowUpIcon fontSize='inherit' />}
              </Stack>
            </TableCell>
            <TableCell sx={styles.headCell}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row, index) => (
            <TableRow key={row.formId ?? `nb-${index}`} data-testid={`p2pFormRow-${row.formId ?? index}`}>
              <TableCell>
                <Typography variant='body2' fontWeight={700} color='text.primary'>{row.submittedByName}</Typography>
                {row.submittedByEmail !== '' && <Typography variant='caption' color='text.secondary'>{row.submittedByEmail}</Typography>}
              </TableCell>
              <TableCell>{row.isd}</TableCell>
              <TableCell>{row.district}</TableCell>
              <TableCell>{row.building}</TableCell>
              <TableCell><StatusChip status={row.status} /></TableCell>
              <TableCell>{row.lastUpdatedAt != null ? new Date(row.lastUpdatedAt).toLocaleDateString() : '—'}</TableCell>
              <TableCell>
                <BuildingActions
                  formId={row.formId} districtOrganizationId={row.districtOrganizationId}
                  reportingYearId={reportingYearId} onChanged={onChanged}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}
